import type { AppointmentStatus } from '@/api/appointments.api';

/**
 * The colour an appointment's STATUS is drawn in on the calendar.
 *
 * Two colours meet on every card and they answer different questions:
 *
 * - the fill is the **service** — see `service-colors.ts` for why it must
 *   never follow the status;
 * - the strip down the left edge is the **status**, and in the two large
 *   calendar styles so is the card's border.
 *
 * Those were the same six values until now, because the card asked
 * `STATUS_META[status].tone` and there are ten statuses to six tones. Four
 * pairs therefore drew identically on the strip — `scheduled`/`cancelled`,
 * `arrived`/`treating`, `pending`/`waiting`, `noshow`/`urgent` — which is
 * exactly the reading the strip exists to give. Each status has its own
 * colour here; `tone` stays what it always was and still carries the
 * semantics everywhere else (a badge, a report, an alert).
 *
 * The values are tokens rather than literals so dark mode is the token
 * file's business, not this file's: `styles/tokens.css`.
 */
export const STATUS_COLOR: Record<AppointmentStatus, string> = {
  pending: 'var(--st-pending)',
  scheduled: 'var(--st-scheduled)',
  confirmed: 'var(--st-confirmed)',
  late: 'var(--st-late)',
  arrived: 'var(--st-arrived)',
  waiting: 'var(--st-waiting)',
  treating: 'var(--st-treating)',
  done: 'var(--st-done)',
  noshow: 'var(--st-noshow)',
  urgent: 'var(--st-urgent)',
  cancelled: 'var(--st-cancelled)',
};

/** The status's colour, or the muted ink if the status is not one of ours. */
export function statusColorFor(status: string | null | undefined): string {
  return STATUS_COLOR[status as AppointmentStatus] ?? 'var(--text-subtle)';
}
